import fetchClient from './fetchClient';
import { errorHandler } from './utils';
import { offlineDb } from 'utils/offlineDb';


const deliveryConfig = () => {
    return {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('delivery-token'),
        },
    };
};

const config = () => {
    return {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
    };
};

export const getPaginatedCustomers = async (
    page: number,
    limit: number,
    search: string,
    opts?: {
      assignedTo?: string;
      tags?: string | string[];
      deliveryTourId?: string;
      statut?: string;
      cibleClient?: string;
      specificiteClient?: string;
      anneeAcquisition?: string;
      paymentMethod?: string;
      billingPeriod?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('limit', String(limit));
    if (search) qs.set('search', search);
    if (opts?.assignedTo) qs.set('assignedTo', opts.assignedTo);

    if (Array.isArray(opts?.tags)) {
      qs.set('tags', opts.tags.join(','));
    } else if (opts?.tags) {
      qs.set('tags', opts.tags);
    }

    if (opts?.deliveryTourId) qs.set('deliveryTourId', opts.deliveryTourId);
    if (opts?.statut) qs.set('statut', opts.statut);
    if (opts?.cibleClient) qs.set('cibleClient', opts.cibleClient);
    if (opts?.specificiteClient) qs.set('specificiteClient', opts.specificiteClient);
    if (opts?.anneeAcquisition) qs.set('anneeAcquisition', opts.anneeAcquisition);
    if (opts?.paymentMethod) qs.set('paymentMethod', opts.paymentMethod);
    if (opts?.billingPeriod) qs.set('billingPeriod', opts.billingPeriod);

    const res: any = await fetchClient
      .get(`/customers?${qs.toString()}`, config())
      .catch(errorHandler);
    if (!res) return null;
    return res.data;
  };
  

export const getCustomerTags = async (): Promise<string[] | null> => {
    const res: any = await fetchClient
      .get('/customers/tags', config())
      .catch(errorHandler);
    if (!res) return null;
    // attend un tableau de strings
    return (res.data?.data || res.data || []) as string[];
  };

export const getCustomerAcquisitionYears = async (): Promise<string[] | null> => {
    const res: any = await fetchClient
      .get('/customers/acquisition-years', config())
      .catch(errorHandler);
    if (!res) return null;
    return (res.data?.data || res.data || []) as string[];
  };

export const checkCustomerCode = async (code: string, excludeId?: string): Promise<boolean> => {
    if (!code) return true;
    const params = excludeId ? `code=${encodeURIComponent(code)}&excludeId=${excludeId}` : `code=${encodeURIComponent(code)}`;
    const res: any = await fetchClient
        .get(`/customers/check-code?${params}`, config())
        .catch((_err: any) => null);
    if (!res) return false;
    return res.data?.available ?? true;
};

export const getPaginatedCustomersByReturnables = async (
    page: number,
    limit: number,
    search: string,
) => {
    const res: any = await fetchClient
        .get(
            `/customers/returnables?limit=${limit}&page=${page}&search=${search}`,
            config(),
        )
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const shareMagicLink = async (email: string, additionalMails: string[] = []) => {
    const res = await fetchClient
        .post(`/auto-orders/share`, { email, additionalMails }, config())
        .catch(errorHandler);
    return res.data;
};

export const getProspects = async (search: string) => {
    const res: any = await fetchClient
        .get(`/customers/prospects?search=${search}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const addCustomerActivity = async (
    id: string,
    content: string,
    type: string,
) => {
    const res = await fetchClient
        .post(`/customers/${id}/activities`, { content, type }, config())
        .catch(errorHandler);
    return res.data;
};

export const addCustomerTasks = async (
    id: string,
    content: string,
    dueDate: string,
) => {
    const res = await fetchClient
        .post(`/customers/${id}/tasks`, { content, dueDate }, config())
        .catch(errorHandler);
    return res.data;
};

export const getCustomers = async () => {
    const res: any = await fetchClient
        .get('/customers?limit=100', config())
        .catch((): null => null);

    if (res?.data) {
        // Cache each customer individually for offline use (non-blocking)
        const list: any[] = res.data?.customers ?? res.data?.docs ?? res.data?.data ?? [];
        if (Array.isArray(list) && list.length > 0)
            offlineDb.cacheCustomers(list).catch((): null => null);
        return res.data;
    }

    // Offline fallback — return all cached customers
    const cached = await offlineDb.getCachedCustomers();
    if (cached && cached.length > 0)
        return { data: cached };
    return null;
};

// Loads a flat array of customers for multi-select pickers (e.g. email campaign modals).
// Uses a higher limit than getCustomers() since SearchMultipleSelector filters client-side.
export const getCustomersForMultiSelect = async (): Promise<any[]> => {
    const res: any = await fetchClient
        .get('/customers?limit=1000', config())
        .catch((): null => null);
    if (res?.data) {
        const list: any[] = res.data?.customers ?? res.data?.docs ?? res.data?.data ?? [];
        return Array.isArray(list) ? list : [];
    }
    const cached = await offlineDb.getCachedCustomers();
    return cached ?? [];
};

// Async search helper for SearchSelector's onSearch prop.
// Returns a flat array of customers (matching the shape SearchSelector expects).
export const searchCustomersForSelect = async (search: string): Promise<any[]> => {
    if (!search.trim()) return [];
    const res = await searchCustomers(search);
    const list: any[] = res?.customers ?? res?.docs ?? res?.data ?? [];
    return list.map((c: any) => ({ ...c, company: c.displayName || c.company }));
};

export const searchCustomers = async (search: string) => {
    const res: any = await fetchClient
        .get(`/customers?limit=50&search=${search}`, config())
        .catch((): null => null);

    if (res?.data) {
        // Keep the cache warm: store any newly returned customers
        const list: any[] = res.data?.customers ?? res.data?.docs ?? res.data?.data ?? [];
        if (Array.isArray(list) && list.length > 0)
            offlineDb.cacheCustomers(list).catch((): null => null);
        return res.data;
    }

    // Offline fallback — filter the local cache by the search term
    const cached = await offlineDb.getCachedCustomers();
    if (cached && cached.length > 0) {
        const q = (search || '').toLowerCase().trim();
        const filtered = q
            ? cached.filter((c: any) => {
                  const name = (c.displayName || c.company || c.name || '').toLowerCase();
                  const email = (c.email || '').toLowerCase();
                  const phone = (c.phone || '').toLowerCase();
                  return name.includes(q) || email.includes(q) || phone.includes(q);
              })
            : cached;
        return { data: filtered.length > 0 ? filtered : cached.slice(0, 50) };
    }
    // Always return a valid shape so callers can safely do res.data.map(...)
    return { data: [] };
};

export const searchCustomersAndProspects = async (search: string) => {
    const res: any = await fetchClient
        .get(`/customers/all?limit=50&search=${search}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getCustomersWithNotInvoicedOrder = async () => {
    const res: any = await fetchClient
        .get('/customers/invoices', config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getCustomer = async (id: string) => {
    const res = await fetchClient.get(`/customers/${id}`, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const getCustomerPage = async (id: string, filter: any = null) => {
    let url = `/customers/page/${id}`;
    if (filter) {
        const { startDate, endDate } = filter;
        url += `?start=${startDate}&end=${endDate}`;
    }

    const res = await fetchClient.get(url, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const getCustomerAnalytics = async (id: string, filter: any = null) => {
    let url = `/customers/${id}/analytics`;
    if (filter) {
        const { startDate, endDate } = filter;
        url += `?start=${startDate}&end=${endDate}`;
    }

    const res = await fetchClient.get(url, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const getCustomerOrderedProducts = async (id: string, filter: any = null) => {
    let url = `/customers/${id}/ordered-products`;
    if (filter) {
        const { startDate, endDate } = filter;
        url += `?start=${startDate}&end=${endDate}`;
    }

    const res = await fetchClient.get(url, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const getCustomerLastOrder = async (id: string) => {
    const res = await fetchClient
        .get(`/customers/${id}/lastOrder`, config())
        .catch((err: any) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const updateCustomerDelivery = async (customer: any) => {
    const id = customer._id;
    delete customer._id;
    const res: any = await fetchClient
        .put(`/customers/${id}`, customer, deliveryConfig())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const updateCustomer = async (customer: any) => {
    const id = customer._id;
    delete customer._id;
    const res: any = await fetchClient
        .put(`/customers/${id}`, customer, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const updatePageActivated = async (customer: any) => {
    const id = customer._id;
    delete customer._id;
    const res: any = await fetchClient
        .put(`/customers/${id}/isOrderingPageActivated`, customer, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const archiveCustomer = async (id: string) => {
    const res: any = await fetchClient
        .delete(`/customers/${id}/archive`, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const getCustomerLikedProducts = async (customerId: string) => {
    const res: any = await fetchClient
        .get(`/customers/${customerId}/liked-products`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const toggleCustomerLikedProduct = async (customerId: string, productId: string) => {
    const res: any = await fetchClient
        .post(`/customers/${customerId}/liked-products/${productId}`, {}, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const deleteCustomer = async (id: string) => {
    const res: any = await fetchClient
        .delete(`/customers/${id}`, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const createCustomer = async (customer: any) => {
    const res = await fetchClient
        .post('/customers', customer, config())
        .catch(errorHandler);
    return res.data;
};

export const importCustomers = async (customers: any) => {
    const res = await fetchClient
        .post('/customers/bulk', { customers }, config())
        .catch(errorHandler);
    return res.data;
};

export const downloadTotalCustomer = async (customerId: string) => {
    const fileConfig: any = {
        headers: {
            Authorization: config().headers.Authorization,
        },
        responseType: 'arraybuffer',
    };
    const res = await fetchClient
        .get(
            `/customers/products-per-year?customerId=${customerId}`,
            fileConfig,
        )
        .catch((err) => {
            errorHandler(err);
        });
    if (!res || !res.data) return null;
    return new Blob([res.data], { type: 'text/csv' });
};

export const downloadCustomersAnalytics = async () => {
  try {
    const res = await fetchClient.get('/customers/export/xlsx/customers-analytics', {
      ...config(),
      responseType: 'blob',
    });
    if (!res || !res.data) return null;
    const cd = (res.headers?.['content-disposition'] || '') as string;
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd || '');
    const filename = decodeURIComponent(match?.[1] || match?.[2] || 'clients-analytics.xlsx');
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return true;
  } catch (err: any) {
    try {
      const blob = err?.response?.data as Blob | undefined;
      const text = blob ? await blob.text() : '';
      if (text) {
        const json = JSON.parse(text);
        errorHandler({ response: { data: { error: json?.error || 'Export impossible' } } });
      } else {
        errorHandler(err);
      }
    } catch {
      errorHandler(err);
    }
    return null;
  }
};

export const downloadAllCustomers = async () => {
  try {
    const res = await fetchClient.get('/customers/export/csv/customers', {
      ...config(),
      responseType: 'blob',
    });

    if (!res || !res.data) return null;

    const cd = (res.headers?.['content-disposition'] || '') as string;
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd || '');
    const filename = decodeURIComponent(match?.[1] || match?.[2] || 'clients.xlsx');

    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return true;
  } catch (err: any) {
    try {
      const blob = err?.response?.data as Blob | undefined;
      const text = blob ? await blob.text() : '';
      if (text) {
        const json = JSON.parse(text);
        errorHandler({ response: { data: { error: json?.error || 'Export impossible' } } });
      } else {
        errorHandler(err);
      }
    } catch {
      errorHandler(err);
    }
    return null;
  }
};